/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Link, Stack, Tooltip, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { StatCardV2 } from '../../../components/v2';
import {
  EMPTY_KPIS,
  buildCategoryCardItems,
  formatBRL,
  formatPercent,
  getCategoryGridSize,
} from './constants';

const SECONDARY_CARDS = [
  {
    key: 'quantidadeNotas',
    kpiKey: 'quantidadeNotas',
    label: 'Qtd. de notas',
    icon: <ReceiptLongIcon />,
    color: 'default',
    format: (v) => String(v ?? 0),
  },
  {
    key: 'ticketMedio',
    kpiKey: 'ticketMedio',
    label: 'Ticket médio · por nota',
    icon: <ShoppingCartOutlinedIcon />,
    color: 'default',
    format: formatBRL,
    nullHint: 'Indisponível sem notas no período',
  },
  {
    key: 'percentualInspecao',
    kpiKey: 'percentualInspecao',
    label: '% em inspeção',
    icon: <FactCheckOutlinedIcon />,
    color: 'warning',
    format: formatPercent,
    nullHint: 'Percentual do total gasto vinculado a inspeção',
  },
];

const PaymentMixBar = ({ viaAporte, viaCartao, totalGasto, loading }) => {
  if (loading || totalGasto <= 0) return null;

  const aportePct = (viaAporte / totalGasto) * 100;
  const cartaoPct = (viaCartao / totalGasto) * 100;
  const other = Math.max(0, 100 - aportePct - cartaoPct);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Composição do pagamento
        </Typography>
        <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
          Aporte {formatPercent(aportePct)}
        </Typography>
        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
          Cartão {formatPercent(cartaoPct)}
        </Typography>
        {other > 0.5 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Outros {formatPercent(other)}
          </Typography>
        )}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          height: 8,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'action.hover',
        }}
        aria-hidden="true"
      >
        {aportePct > 0 && (
          <Box sx={{ width: `${aportePct}%`, bgcolor: 'info.main' }} />
        )}
        {cartaoPct > 0 && (
          <Box sx={{ width: `${cartaoPct}%`, bgcolor: 'success.main' }} />
        )}
        {other > 0 && (
          <Box sx={{ width: `${other}%`, bgcolor: 'grey.400' }} />
        )}
      </Box>
    </Box>
  );
};

PaymentMixBar.propTypes = {
  viaAporte: PropTypes.number,
  viaCartao: PropTypes.number,
  totalGasto: PropTypes.number,
  loading: PropTypes.bool,
};

PaymentMixBar.defaultProps = {
  viaAporte: 0,
  viaCartao: 0,
  totalGasto: 0,
  loading: false,
};

const KpiStrip = ({
  kpis,
  categoryKpis,
  loading,
  activeCategoria,
  onSelectCategoria,
}) => {
  const [showZeroCategories, setShowZeroCategories] = useState(false);
  const data = kpis || EMPTY_KPIS;
  const categoryData = categoryKpis || data;

  const totalGasto = Number(data.totalGasto) || 0;
  const viaAporte = Number(data.viaAporte) || 0;
  const viaCartao = Number(data.viaCartao) || 0;

  const aportePercent = totalGasto > 0 ? (viaAporte / totalGasto) * 100 : 0;
  const cartaoPercent = totalGasto > 0 ? (viaCartao / totalGasto) * 100 : 0;

  const categoryItems = useMemo(
    () => buildCategoryCardItems(categoryData, {
      hideZero: !showZeroCategories,
      sort: 'desc',
    }),
    [categoryData, showZeroCategories],
  );

  const categoryGridSize = useMemo(
    () => getCategoryGridSize(categoryItems.length),
    [categoryItems.length],
  );

  const hasHiddenZeroCategories = useMemo(
    () => buildCategoryCardItems(categoryData, { hideZero: true }).length
      < buildCategoryCardItems(categoryData, { hideZero: false }).length,
    [categoryData],
  );

  const handleClick = (label) => {
    if (!onSelectCategoria) return;
    onSelectCategoria(activeCategoria === label ? null : label);
  };

  return (
    <Box>
      {/* Hero: Total + canais de pagamento */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCardV2
            label="Total no período"
            value={formatBRL(totalGasto)}
            icon={<AttachMoneyIcon />}
            color="primary"
            loading={loading}
            valueFontSize="1.75rem"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCardV2
            label={totalGasto > 0 ? `Via aporte · ${formatPercent(aportePercent)}` : 'Via aporte'}
            value={formatBRL(viaAporte)}
            icon={<AccountBalanceWalletIcon />}
            color="info"
            loading={loading}
            valueFontSize="1.25rem"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <StatCardV2
            label={totalGasto > 0 ? `Via cartão · ${formatPercent(cartaoPercent)}` : 'Via cartão'}
            value={formatBRL(viaCartao)}
            icon={<CreditCardIcon />}
            color="success"
            loading={loading}
            valueFontSize="1.25rem"
          />
        </Grid>
      </Grid>

      <PaymentMixBar
        viaAporte={viaAporte}
        viaCartao={viaCartao}
        totalGasto={totalGasto}
        loading={loading}
      />

      {/* Métricas secundárias */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block', mt: 2.5, mb: 1 }}
      >
        Volume e qualidade
      </Typography>
      <Grid container spacing={2}>
        {SECONDARY_CARDS.map((card) => {
          const rawValue = data[card.kpiKey];
          const isNullMetric = rawValue == null && card.nullHint;
          const displayValue = isNullMetric ? '—' : card.format(rawValue ?? 0);

          const cardNode = (
            <StatCardV2
              label={card.label}
              value={displayValue}
              icon={card.icon}
              color={card.color}
              loading={loading}
              valueFontSize="1.15rem"
            />
          );

          return (
            <Grid key={card.key} size={{ xs: 12, sm: 4 }}>
              {isNullMetric ? (
                <Tooltip title={card.nullHint} arrow placement="top">
                  <span>{cardNode}</span>
                </Tooltip>
              ) : (
                cardNode
              )}
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mt: 2.5,
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase' }}
        >
          Por categoria — clique para filtrar o painel
        </Typography>
        {hasHiddenZeroCategories && (
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => setShowZeroCategories((prev) => !prev)}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          >
            {showZeroCategories ? 'Ocultar zeradas' : 'Mostrar zeradas'}
          </Link>
        )}
      </Box>

      {!loading && categoryItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nenhuma categoria com gasto no período selecionado.
        </Typography>
      ) : (
        <Grid container spacing={1}>
          {categoryItems.map((item) => {
            const isActive = activeCategoria === item.label;
            const percentLabel = item.valor > 0 ? formatPercent(item.percentual) : null;
            const cardLabel = percentLabel ? `${item.label} · ${percentLabel}` : item.label;

            return (
              <Grid key={item.enum} size={categoryGridSize}>
                <StatCardV2
                  label={cardLabel}
                  value={formatBRL(item.valor)}
                  color={item.color}
                  loading={loading}
                  active={isActive}
                  activeLabel={isActive ? 'Filtro ativo — clique para remover' : undefined}
                  valueFontSize="1.1rem"
                  onClick={() => handleClick(item.label)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

KpiStrip.propTypes = {
  kpis: PropTypes.object,
  categoryKpis: PropTypes.object,
  loading: PropTypes.bool,
  activeCategoria: PropTypes.string,
  onSelectCategoria: PropTypes.func,
};

KpiStrip.defaultProps = {
  kpis: EMPTY_KPIS,
  categoryKpis: null,
  loading: false,
  activeCategoria: null,
  onSelectCategoria: null,
};

export default KpiStrip;
